import React, { useState, useMemo, useEffect } from "react";
import { usePriceContext } from "@/context/PriceContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ArrowUp, ArrowDown, X, Star, SortAsc, SortDesc, Plus, GripVertical, FileText } from "lucide-react";
import TradingCard from "@/components/TradingCard";
import TradingCardHeader from "@/components/TradingCardHeader";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import TradingModal from "@/components/TradingModal";
import { apiClient } from "@/utils/apiClient";
import { SymbolAIChatDrawer } from './SymbolAIChatDrawer';

import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from "@/components/ui/collapsible";
import { 
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell
} from "@/components/ui/table";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue,
  SelectGroup,
  SelectLabel
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import ColumnChooser, { ColumnDefinition } from "@/components/ColumnChooser";
import { useWatchlist } from "@/context/WatchlistContext";
import { useLocation } from "react-router-dom";
import { TradingSymbol } from "@/utils/mockData";

// Declare global TradingView type
declare global {
  interface Window {
    TradingView: any;
  }
}

// Define interface for instrument API response
interface Instrument {
  id: string;
  name: string;
  sequence: number;
}

// Define all possible columns
const ALL_COLUMNS: ColumnDefinition[] = [
  { id: "name", label: "Symbol", group: "Primary" },
  { id: "price", label: "LTP", group: "Primary" },
  { id: "buyPrice", label: "Bid", group: "Primary" },
  { id: "sellPrice", label: "Ask", group: "Primary" },
  { id: "bidVolume", label: "Bid Vol", group: "Volume" },
  { id: "askVolume", label: "Ask Vol", group: "Volume" },
  { id: "high", label: "High", group: "Range" },
  { id: "low", label: "Low", group: "Range" },
  { id: "open", label: "Open", group: "Reference" },
  { id: "close", label: "Close", group: "Reference" },
  { id: "lowerCircuit", label: "Lower Circuit", group: "Limits" },
  { id: "upperCircuit", label: "Upper Circuit", group: "Limits" },
  { id: "spread", label: "Spread", group: "Derived" },
  { id: "absChange", label: "Diff (Abs)", group: "Changes" },
  { id: "percentChange", label: "Diff (%)", group: "Changes" },
  { id: "lastTrade", label: "Last Trade", group: "Time" },
  { id: "expiry", label: "Expiry", group: "Time" },
  { id: "openInterest", label: "Open Interest", group: "Market Data" },
  { id: "volume", label: "Volume", group: "Market Data" },
  { id: "exchange", label: "Exchange", group: "Reference" },
];

// Default visible columns
const DEFAULT_VISIBLE_COLUMNS = [
  "name", "price", "buyPrice", "sellPrice", "bidVolume", "askVolume", "high", "low", 
  "spread", "absChange", "percentChange", "lastTrade"
];

// Default column order (same as visible columns initially)
const DEFAULT_COLUMN_ORDER = [...DEFAULT_VISIBLE_COLUMNS];

// Define props interface for the TradingFeed component
interface TradingFeedProps {
  hideStarInWatchlist?: boolean;
}

// Trading Feed component that uses the context
export const TradingFeed: React.FC<TradingFeedProps> = ({ hideStarInWatchlist }) => {
  
  const { symbols, refreshData, isConnected } = usePriceContext();
  const { activeView, watchlistSymbols, addToWatchlist, addMultipleToWatchlist, removeFromWatchlist, isInWatchlist } = useWatchlist();
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogSearchTerm, setDialogSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedSymbols, setSelectedSymbols] = useState<string[]>([]);
  const location = useLocation();
  
  // Determine if we're on watchlist page or market watch page
  const isWatchlistPage = location.pathname === "/watchlist";
  
  const [instrumentTypeFilter, setInstrumentTypeFilter] = useState<string>("all");
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [instrumentsLoading, setInstrumentsLoading] = useState(false);
  
  // State for sorting
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null);
  const [sortField, setSortField] = useState<string>("name");
  
  // State for pagination
  const [visibleSymbolsCount, setVisibleSymbolsCount] = useState(8);
  
  // State for visible columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    // Try to load from localStorage
    const saved = localStorage.getItem("visibleTableColumns");
    return saved ? JSON.parse(saved) : DEFAULT_VISIBLE_COLUMNS;
  });

  // State for column order
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    // Try to load from localStorage
    const savedOrder = localStorage.getItem("columnOrder");
    return savedOrder ? JSON.parse(savedOrder) : DEFAULT_COLUMN_ORDER;
  });
  
  // Check if mobile view should be used
  const isMobile = useIsMobile();
  
  // State for chart modal
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedSymbolForChart, setSelectedSymbolForChart] = useState("");
  const [tradingViewWidget, setTradingViewWidget] = useState<any>(null);
  
  // State for AI chat drawer
  const [aiChatSymbol, setAIChatSymbol] = useState<TradingSymbol | null>(null);
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);

  // Effect to load TradingView widget when modal opens
  useEffect(() => {
    if (chartModalOpen && selectedSymbolForChart) {
      // Destroy existing widget if it exists
      if (tradingViewWidget) {
        try {
          tradingViewWidget.remove();
        } catch (e) {
          console.log('Error removing previous widget:', e);
        }
        setTradingViewWidget(null);
      }

      // Clear the container
      const container = document.getElementById("tradingview_widget");
      if (container) {
        container.innerHTML = "";
      }

      // Load TradingView script if not already loaded
      if (!window.TradingView) {
        const script = document.createElement("script");
        script.src = "https://s3.tradingview.com/tv.js";
        script.onload = () => {
          setTimeout(() => createTradingViewWidget(), 100);
        };
        document.head.appendChild(script);
      } else {
        setTimeout(() => createTradingViewWidget(), 100);
      }
    }

    // Cleanup when modal closes
    if (!chartModalOpen && tradingViewWidget) {
      try {
        tradingViewWidget.remove();
      } catch (e) {
        console.log('Error removing widget on close:', e);
      }
      setTradingViewWidget(null);
    }
  }, [chartModalOpen, selectedSymbolForChart]);

  const createTradingViewWidget = () => {
    if (window.TradingView && selectedSymbolForChart) {
      try {
        const localTheme = localStorage.getItem("theme") || "dark";
        const widget = new window.TradingView.widget({
          container_id: "tradingview_widget",
          width: "100%",
          height: 500,
          symbol: selectedSymbolForChart.toUpperCase(),
          interval: "D",
          timezone: "Etc/UTC",
          theme: localTheme, // <-- Use the theme from localStorage
          style: "1",
          locale: "en",
          enable_publishing: false,
          allow_symbol_change: false,
          hide_top_toolbar: true,
          details: true,
          hotlist: true,
          calendar: true,
          studies: ["MACD@tv-basicstudies"],
          // Custom styling for better colors
          overrides: {
            "paneProperties.background": "#1a1a1a",
            "paneProperties.vertGridProperties.color": "#363636",
            "paneProperties.horzGridProperties.color": "#363636",
            "symbolWatermarkProperties.transparency": 90,
            "scalesProperties.textColor": "#AAA",
            "mainSeriesProperties.candleStyle.upColor": "#00C851",
            "mainSeriesProperties.candleStyle.downColor": "#ff4444",
            "mainSeriesProperties.candleStyle.borderUpColor": "#00C851",
            "mainSeriesProperties.candleStyle.borderDownColor": "#ff4444",
            "mainSeriesProperties.candleStyle.wickUpColor": "#00C851",
            "mainSeriesProperties.candleStyle.wickDownColor": "#ff4444"
          }
        });
        setTradingViewWidget(widget);
      } catch (error) {
        console.error('Error creating TradingView widget:', error);
      }
    }
  };
  
  // Fetch instruments from API
  useEffect(() => {
    const fetchInstruments = async () => {
      setInstrumentsLoading(true);
      try {
        const data: Instrument[] = await apiClient.get('instruments');
        
        // Sort instruments by sequence number
        // Items with sequence 0 or undefined go to the end
        const sortedInstruments = data.sort((a, b) => {
          const seqA = a.sequence || 0;
          const seqB = b.sequence || 0;
          
          // If both have sequence 0, maintain original order
          if (seqA === 0 && seqB === 0) return 0;
          // Items with sequence 0 go to the end
          if (seqA === 0) return 1;
          if (seqB === 0) return -1;
          // Normal ascending sort for non-zero sequences
          return seqA - seqB;
        });
        
        setInstruments(sortedInstruments);
      } catch (error) {
        console.error('Error fetching instruments:', error);
        toast.error('Error loading instrument types');
      } finally {
        setInstrumentsLoading(false);
      }
    };

    fetchInstruments();
  }, []);
  
  // Reset filters when tab changes or route changes
  useEffect(() => {
    // Reset all filters when activeView changes or when navigating between pages
    setSearchTerm("");
    setInstrumentTypeFilter("all");
    setSortDirection(null);
    setSortField("name");
    setVisibleSymbolsCount(isMobile ? 8 : 20);
    
    // Don't reset column visibility as that's more of a UI preference
    
    // Show a toast notification when switching tabs (only on market watch)
    if (location.pathname === "/") {
      // toast.info(`Switched to ${activeView === "market" ? "Market View" : "Favorites"}`);
    }
  }, [activeView, location.pathname, isMobile]);
  
  useEffect(() => {
    localStorage.setItem("visibleTableColumns", JSON.stringify(visibleColumns));
  }, [visibleColumns]);
  
  // Save column order to localStorage
  useEffect(() => {
    localStorage.setItem("columnOrder", JSON.stringify(columnOrder));
  }, [columnOrder]);
  
  // Create instrument types array with "all" option first, then sorted instruments
  const instrumentTypes = useMemo(() => {
    return ["all", ...instruments.map(instrument => instrument.name)];
  }, [instruments]);
  
  // Toggle sort direction for a specific field
  const toggleSort = (field: string) => {
    setSortField(field);
    setSortDirection(prev => {
      if (prev === null || field !== sortField) return "asc";
      if (prev === "asc") return "desc";
      return null;
    });
  };
  
  // Toggle favorite status (add/remove from watchlist)
  const toggleFavorite = async (symbolName: string) => {
    console.log('TradingFeed: toggleFavorite called for:', symbolName);
    
    const isFavorite = isInWatchlist(symbolName);
    
    try {
      // Make API call with new format
      await apiClient.post('favourites', { 
        symbol: symbolName,
        favourite: !isFavorite 
      });
      
      // Update local state
      if (isFavorite) {
        removeFromWatchlist(symbolName);
      } else {
        addToWatchlist(symbolName);
      }
      
      toast.success(`${symbolName} ${!isFavorite ? 'added to' : 'removed from'} favorites`);
    } catch (error) {
      console.error('TradingFeed: Error toggling favorite:', error);
      toast.error(`Failed to ${!isFavorite ? 'add' : 'remove'} ${symbolName}`);
    }
  };

  const getRelatedBidColor = (currentBid: number | string, previousBid: number | null): string => {
    if (typeof currentBid === 'string' || !previousBid) return "text-theme-light";
    if (currentBid > previousBid) return "text-positive hardik";
    if (currentBid < previousBid) return "text-negative patel";
    return "text-theme-light";
  };

   
  const getRelatedAskColor = (currentAsk: number | string, previousAsk: number | null): string => {
    if (typeof currentAsk === 'string' || !previousAsk) return "text-theme-light";
    if (currentAsk > previousAsk) return "text-positive hardik22";
    if (currentAsk < previousAsk) return "text-negative patel11";
    return "text-theme-light";
  };

  const formatPrice = (price: number | string): string => {
    if (typeof price === 'string') return price;
    
    if (price >= 1000) {
      return price.toFixed(1);
    } else if (price >= 100) {
      return price.toFixed(1);
    } else if (price >= 10) {
      return price.toFixed(2);
    } else {
      return price.toFixed(price < 1 ? 4 : 2);
    }
  };

  const formatVolume = (volume?: number): string => {
    if (volume === undefined || volume === null) return "--";
    
    if (volume >= 1000000) {
      return `${(volume / 1000000).toFixed(2)}M`;
    } else if (volume >= 1000) {
      return `${(volume / 1000).toFixed(2)}K`;
    } else {
      return volume.toFixed(2);
    }
  };

  const getVolumeChangeClass = (current?: number, previous?: number): string => {
    if (current === undefined || previous === undefined) return "text-muted-foreground";
    if (current > previous) return "text-positive animate-fade-in";
    if (current < previous) return "text-negative animate-fade-in";
    return "text-muted-foreground";
  };

  const handleAddSelectedSymbols = async () => {
    if (selectedSymbols.length === 0) {
      toast.error("No symbols selected");
      return;
    }
    
    // Add all selected symbols to the watchlist in a single API call
    await addMultipleToWatchlist(selectedSymbols);
    
    // Reset state
    setSelectedSymbols([]);
    setSearchValue("");
    setIsAddDialogOpen(false);
  };

  const handleSymbolToggle = (symbolName: string) => {
    setSelectedSymbols(prev => 
      prev.includes(symbolName) 
        ? prev.filter(s => s !== symbolName)
        : [...prev, symbolName]
    );
  };

  // Handle column reordering
  const handleColumnReorder = (fromIndex: number, toIndex: number) => {
    const updatedOrder = [...columnOrder];
    const [movedItem] = updatedOrder.splice(fromIndex, 1);
    updatedOrder.splice(toIndex, 0, movedItem);
    setColumnOrder(updatedOrder);
    
    toast.success("Column order updated", {
      id: "column-reorder",
      duration: 2000,
    });
  };
  
  // Filter symbols based on search, instrument type, and active view (market or favorites)
  const filteredSymbols = useMemo(() => {
    let filtered = symbols;

    // First filter by active view (market shows all, favorites shows only favorites)
    // On watchlist page, we always show watchlist items
    if (isWatchlistPage || activeView === "favorites") {
      filtered = filtered.filter(symbol => isInWatchlist(symbol.name));
    }

    // Then apply search and instrument type filters
    filtered = filtered.filter(symbol => {
      const matchesSearch = symbol.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = instrumentTypeFilter === "all" || symbol.intrumentType === instrumentTypeFilter;
      return matchesSearch && matchesType;
    });
    
    // Apply sorting if needed
    if (sortDirection !== null) {
      filtered = [...filtered].sort((a, b) => {
        let comparison = 0;
        
        switch (sortField) {
          case "name":
            comparison = a.name.localeCompare(b.name);
            break;
          case "price":
            const aPrice = (a.buyPrice + a.sellPrice) / 2;
            const bPrice = (b.buyPrice + b.sellPrice) / 2;
            comparison = aPrice - bPrice;
            break;
          case "spread":
            comparison = a.spread - b.spread;
            break;
          case "change":
            comparison = a.change - b.change;
            break;
          case "buyPrice":
            comparison = a.buyPrice - b.buyPrice;
            break;
          case "sellPrice":
            comparison = a.sellPrice - b.sellPrice;
            break;
          case "high":
            comparison = a.high - b.high;
            break;
          case "low":
            comparison = a.low - b.low;
            break;
          case "bidVolume":
            const aBidVol = a.bidVolume || 0;
            const bBidVol = b.bidVolume || 0;
            comparison = aBidVol - bBidVol;
            break;
          case "askVolume":
            const aAskVol = a.askVolume || 0;
            const bAskVol = b.askVolume || 0;
            comparison = aAskVol - bAskVol;
            break;
          default:
            comparison = a.name.localeCompare(b.name);
        }
        
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }
    
    return filtered;
  }, [symbols, searchTerm, instrumentTypeFilter, activeView, watchlistSymbols, sortDirection, sortField, isInWatchlist, isWatchlistPage]);
  
  
const allSymbols = usePriceContext()?.symbols || [];


// console.log('allSymbols....',allSymbols);

    const oldfilteredSymbols = allSymbols
  .filter(symbol => {
    const name = symbol?.name?.toUpperCase();
    const search = searchValue.toLowerCase();
    return (
      symbol?.name?.toLowerCase().includes(search) &&
      !(watchlistSymbols || []).includes(name)
    );
  })
  .slice(0, 50);

      // console.log('oldfilteredSymbols....',oldfilteredSymbols);
  
  
  
  // Limit the number of visible symbols
  const visibleSymbols = useMemo(() => {
    const count = isMobile ? Math.max(8, visibleSymbolsCount) : visibleSymbolsCount;
    return filteredSymbols.slice(0, count);
  }, [filteredSymbols, visibleSymbolsCount, isMobile]);

  // Check if there are more symbols to load
  const hasMoreSymbols = visibleSymbolsCount < filteredSymbols.length;
  
  const handleViewMore = () => {
    setVisibleSymbolsCount(prevCount => prevCount + (isMobile ? 4 : 20));
  };
  
  // Reset visible count when filters change
  useEffect(() => {
    setVisibleSymbolsCount(isMobile ? 8 : 20);
  }, [searchTerm, instrumentTypeFilter, activeView, isMobile]);
  
  // Function to clear search input
  const clearSearch = () => {
    setSearchTerm("");
  };

  const clearDialogSearch = () => {
    setDialogSearchTerm("");
  };
  
  // Handle Ask AI click
  const handleAskAIClick = (symbol: TradingSymbol) => {
    setAIChatSymbol(symbol);
    setIsAIChatOpen(true);
  };
  
  const renderSortIndicator = (field: string) => {
    if (sortField !== field || sortDirection === null) {
      return null;
    }
    
    return sortDirection === "asc" ? 
      <SortAsc className="inline ml-1" size={14} /> : 
      <SortDesc className="inline ml-1" size={14} />;
  };

  // Handle column visibility changes
  const handleColumnVisibilityChange = (columns: string[]) => {
    if (!columns.includes("name")) {
      columns.push("name");
    }
    setVisibleColumns(columns);
    
    // Update column order based on new visibility
    const newOrder = columnOrder.filter(col => columns.includes(col));
    const newColumns = columns.filter(col => !newOrder.includes(col));
    setColumnOrder([...newOrder, ...newColumns]);
  };

  // Get the ordered visible columns for rendering
  const orderedVisibleColumns = useMemo(() => {
    return columnOrder
      .filter(colId => visibleColumns.includes(colId))
      // Add any visible columns that aren't in the order yet (fallback)
      .concat(visibleColumns.filter(colId => !columnOrder.includes(colId)));
  }, [columnOrder, visibleColumns]);

  // Get column definition by ID
  const getColumnDefinition = (columnId: string) => {
    return ALL_COLUMNS.find(col => col.id === columnId) || { id: columnId, label: columnId, group: "Other" };
  };

  return (
    <div className=" mx-auto px-1 sm:px-4 py-2">
      
      <div className="mb-2  space-y-1.5 sticky top-search top-0 z-30 pt-1 pb-1 bg-background">
        {/* Responsive search and filter area */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
          <div className="flex col-span-1 sm:col-span-2 gap-1.5">
            {/* Search box with clear button */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder="Search symbols..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Clear search"
                >
                  <X size={18} />
                </button>
              )}
            </div>
            
            <div className="w-32 sm:w-48">
              <Select value={instrumentTypeFilter} onValueChange={setInstrumentTypeFilter} disabled={instrumentsLoading}>
                <SelectTrigger>
                  <SelectValue placeholder={instrumentsLoading ? "Loading..." : "Filter by type"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Instrument Types</SelectLabel>
                    <SelectItem value="all">All Types</SelectItem>
                    {instruments.map((instrument) => (
                      <SelectItem key={instrument.id} value={instrument.name}>
                        {instrument.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="col-span-1 flex justify-between gap-2">
            {(isWatchlistPage || activeView === "favorites") && (
              <Button onClick={() => setIsAddDialogOpen(true)} variant="outline" className="w-full md:w-[70%] flex items-center">
                <Plus className="mr-2 h-4 w-4" />
                Add Symbols
              </Button>                
            )}

            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Add Symbols to Watchlist</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <Command className="rounded-lg border shadow-md">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                      <Input
                        placeholder="Search symbols..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="pl-10 pr-10"
                      />
                    </div>
                    {oldfilteredSymbols.length > 0 ? (
                      <div className="border-t">
                        <div className="py-2 px-2 flex justify-between items-center border-b">
                          <span className="text-sm font-medium">Select symbols</span>
                          <div className="flex space-x-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-xs h-7"
                              onClick={() => setSelectedSymbols(oldfilteredSymbols.map(s => s.name))}
                            >
                              Select All
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-xs h-7"
                              onClick={() => setSelectedSymbols([])}
                            >
                              Clear
                            </Button>
                          </div>
                        </div>
                        <ScrollArea className="h-[300px]">
                          <CommandList>
                            <CommandGroup>
                              {oldfilteredSymbols.map((symbol) => (
                                <CommandItem
                                  key={symbol.name}
                                  value={symbol.name}
                                  onSelect={() => handleSymbolToggle(symbol.name)}
                                  className="flex items-center justify-between cursor-pointer"
                                >
                                  <div className="flex items-center space-x-2">
                                    <Checkbox 
                                      checked={selectedSymbols.includes(symbol.name)}
                                      onCheckedChange={() => handleSymbolToggle(symbol.name)}
                                      id={`symbol-${symbol.name}`}
                                    />
                                    <label htmlFor={`symbol-${symbol.name}`} className="text-sm cursor-pointer flex-1">
                                      {symbol.name}
                                    </label>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </ScrollArea>
                      </div>
                    ) : searchValue ? (
                      <div className="py-6 px-4 text-center">
                        <div className="flex flex-col items-center justify-center text-muted-foreground">
                          <Search className="h-12 w-12 mb-3 opacity-40" />
                          <p className="text-sm font-medium">No symbols found</p>
                          <p className="text-xs mt-1">Try a different search term</p>
                        </div>
                      </div>
                    ) : (
                      <CommandEmpty className="py-6 text-center text-sm">
                        Type to search for symbols
                      </CommandEmpty>
                    )}
                  </Command>
                </div>
                
                {selectedSymbols.length > 0 && (
                  <div className="flex items-center justify-between py-2 px-1">
                    <span className="text-sm text-muted-foreground">
                      {selectedSymbols.length} symbol{selectedSymbols.length !== 1 ? 's' : ''} selected
                    </span>
                  </div>
                )}
                
                <DialogFooter className="flex justify-between space-x-2">
                  <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleAddSelectedSymbols} 
                    disabled={selectedSymbols.length === 0}
                  >
                    Add Selected
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* Column Chooser */}
            <ColumnChooser 
              columns={ALL_COLUMNS}
              visibleColumns={visibleColumns}
              onVisibleColumnsChange={handleColumnVisibilityChange}
            />
          </div>
        </div>
      </div>
      
      {/* Enhanced Desktop Table Layout */}
      {!isMobile && (
        <div className="hidden md:block border rounded-lg overflow-hidden">
          <div className="max-h-[calc(100vh-180px)] overflow-auto relative">
            <div className="min-w-max">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 sticky left-0 z-20 bg-background"></TableHead>
                    {orderedVisibleColumns.map((colId, index) => {
                      const col = getColumnDefinition(colId);
                      
                      // Special case for name column which is always sticky
                      if (colId === "name") {
                        return (
                          <TableHead 
                            key={colId} 
                            className="cursor-pointer sticky left-10 z-20 bg-background sticky left-0 z-10"
                            onClick={() => toggleSort(colId)}
                            draggable={true}
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', index.toString());
                              e.currentTarget.classList.add('opacity-50');
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                            }}
                            onDragEnd={(e) => {
                              e.currentTarget.classList.remove('opacity-50');
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const fromIndex = Number(e.dataTransfer.getData('text/plain'));
                              if (fromIndex !== index) {
                                handleColumnReorder(fromIndex, index);
                              }
                            }}
                          >
                            <div className="flex items-center gap-1 sticky left-0 z-20  ">
                              <GripVertical className="h-3 w-3 opacity-50 cursor-move" />
                              Symbol {renderSortIndicator(colId)}
                            </div>
                          </TableHead>
                        );
                      }
                      
                      // All other columns
                      return (
                        <TableHead 
                          key={colId} 
                          className={`text-right cursor-pointer ${colId === "bidVolume" || colId === "askVolume" ? "whitespace-nowrap" : ""}`}
                          onClick={() => toggleSort(colId)}
                          draggable={true}
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', index.toString());
                            e.currentTarget.classList.add('opacity-50');
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                          }}
                          onDragEnd={(e) => {
                            e.currentTarget.classList.remove('opacity-50');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const fromIndex = Number(e.dataTransfer.getData('text/plain'));
                            if (fromIndex !== index) {
                              handleColumnReorder(fromIndex, index);
                            }
                          }}
                        >
                          <div className="flex items-center justify-end gap-1">
                            <GripVertical className="h-3 w-3 opacity-50 cursor-move" />
                            {col.label} {renderSortIndicator(colId)}
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleSymbols.length > 0 ? (
                    visibleSymbols.map((symbol) => {
                      
                      // Calculate absolute price difference
                      const lastTradePrice = (symbol.buyPrice + symbol.sellPrice) / 2;
                      const absoluteDifference = (symbol.change / 100) * lastTradePrice;
                      
                      // Format the time from symbol's lastTradeTime
                      const formattedTime = symbol.lastTradeTime ? (() => {
                        const date = new Date(symbol.lastTradeTime);
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        const seconds = String(date.getSeconds()).padStart(2, '0');
                        const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
                        return `${hours}:${minutes}:${seconds}.${milliseconds}`;
                      })() : "--:--:--.---";
                      // console.log('formattedTime...',symbol.lastTradeTime);
                      // Use real volume data from symbol, with fallbacks to mocked data
                      const bidVolume = symbol.bidVolume !== undefined ? symbol.bidVolume : Math.floor(Math.random() * 1000) + 100;
                      const askVolume = symbol.askVolume !== undefined ? symbol.askVolume : Math.floor(Math.random() * 1000) + 100;
                      
                      // Determine CSS classes for volume changes
                      const bidVolumeClass = getVolumeChangeClass(symbol.bidVolume, symbol.previousBidVolume);
                      const askVolumeClass = getVolumeChangeClass(symbol.askVolume, symbol.previousAskVolume);
                      
                      // Mock values for the new fields
                      const openPrice = symbol.open;
                      const closePrice = symbol.close;
                      const lowerCircuit = (symbol.low * 0.8).toFixed(2);
                      const upperCircuit = (symbol.high * 1.2).toFixed(2);
                      const openInterest = symbol.openInterest;
                      const volume = symbol.volume;
                      const exchange = ["NSE", "BSE", "NYSE", "NASDAQ"][Math.floor(Math.random() * 4)];
                      
                      const isBadlaInstrument = symbol.intrumentType === "BADLA";

                      let exchangeData = {};
   
                      // Process related symbols if available
                      if (symbol.relatedSymbols) {
                        const relatedSymbolsString = typeof symbol.relatedSymbols === 'string' ? symbol.relatedSymbols : '';
                        const relatedList = relatedSymbolsString.replace(/[{}]/g, "").trim();
                        const relatedArray = relatedList ? relatedList.split(",") : [];
                        
                        if (relatedArray.length > 0) {
                          relatedArray.forEach(relatedSymbol => {
                            // Find the related symbol in the symbols array
                            const relatedSymbolData = symbols.find(s => s.name === relatedSymbol);
                            
                            exchangeData[relatedSymbol] = {
                              bid: relatedSymbolData ? relatedSymbolData.buyPrice : "--",
                              ask: relatedSymbolData ? relatedSymbolData.sellPrice : "--",
                              // Store previous prices for color comparison
                              previousBid: relatedSymbolData ? relatedSymbolData.previousBuyPrice : null,
                              previousAsk: relatedSymbolData ? relatedSymbolData.previousSellPrice : null,
                            };
                          });      
                        }
                      }

                      return (
                        <TableRow 
                          key={symbol.id}
                          className="hover:bg-card/30 transition-all animate-fade-in"
                        >
                          
                          <TableCell className="p-2 sticky left-0 z-10 bg-background">
                            <div className="flex items-center gap-1">
                              {/* Favorite button */}
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6 p-0" 
                                onClick={(e) => {
                                  console.log('TradingFeed: Star button clicked for:', symbol.name);
                                  toggleFavorite(symbol.name);
                                }}
                                aria-label={isInWatchlist(symbol.name) ? "Remove from watchlist" : "Add to watchlist"}
                              >
                                <Star 
                                  size={16} 
                                  className={isInWatchlist(symbol.name) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"} 
                                />
                              </Button>
                              
                              {/* Chart report button - hidden on mobile */}
                              {/*{!isMobile && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-6 w-6 p-0" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedSymbolForChart(symbol.name);
                                    setChartModalOpen(true);
                                  }}
                                  aria-label="View chart"
                                >
                                {/* setSelectedSymbolForChart(`${symbol.intrumentType}:${symbol.name}`); */}
                               
                                 {/* <FileText 
                                    size={16} 
                                    className="text-muted-foreground" 
                                  />
                                </Button>
                              )}*/}
                            </div>
                          </TableCell>

                          {/* Render cells based on ordered visible columns */}
                          {orderedVisibleColumns.map(colId => {
                            switch (colId) {
                              case "name":
                                return (
                                  <TableCell key={colId} className="font-medium sticky left-10 z-10 bg-background sticky left-0 z-10">
                                    <span 
                                      className="cursor-pointer hover:text-primary transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedSymbolForChart(symbol.name);
                                        setChartModalOpen(true);
                                      }}
                                    >
                                      {symbol.name} 
                                    </span>
                                    
                                    {/* Expandable content for BADLA instruments */}
                                    {Object.keys(exchangeData).length > 0 && isBadlaInstrument && !isMobile && (
                                      <Collapsible
                                        open={isExpanded}
                                        onOpenChange={setIsExpanded}
                                        className="w-full"
                                      >
                                        {/* Mobile Expandable Trigger */}
                                        <div className="w-full text-center py-1 border-t border-border/30">
                                          <CollapsibleTrigger asChild>
                                            <Button 
                                              variant="ghost" 
                                              size="sm" 
                                              className="h-5 w-5 p-0 text-theme-secondary hover:text-theme-light focus:ring-0 focus:outline-none" 
                                              aria-label={isExpanded ? "Collapse" : "Expand"}
                                            >
                                              <ArrowDown 
                                                size={14} 
                                                className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                              />
                                            </Button>
                                          </CollapsibleTrigger>
                                        </div>
                                        
                                        {/* Mobile Expandable Content - Color-coded prices */}
                                        <CollapsibleContent className="overflow-hidden transition-all animate-accordion-down">
                                          <div className="p-2 pt-0 border-t border-border/30 mt-1 bg-card/70">
                                            {/* Exchange Prices Header */}
                                            <div className="grid grid-cols-3 gap-2 mb-1 text-xs font-medium text-theme-light">
                                              <div>Symbol</div>
                                              <div className="text-center">Bid</div>
                                              <div className="text-right">Ask</div>
                                            </div>
                                            
                                            {/* Exchange Price Rows - Color-coded prices */}
                                            <div className="space-y-1">
                                              {Object.entries(exchangeData).map(([exchange, prices]: [string, any]) => {
                                                const bidColorClass = getRelatedBidColor(prices.bid, prices.previousBid);
                                                const askColorClass = getRelatedAskColor(prices.ask, prices.previousAsk);
                                                
                                                return (
                                                  <div key={exchange} className="grid grid-cols-3 gap-2 text-sm">
                                                    <div className="font-medium truncate text-theme-light">{exchange}</div>
                                                    <div className={`text-center ${bidColorClass}`}>
                                                      {formatPrice(prices.bid)}
                                                    </div>
                                                    <div className={`text-right ${askColorClass}`}>
                                                      {formatPrice(prices.ask)}
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        </CollapsibleContent>
                                      </Collapsible>
                                    )}                              
                                  </TableCell>

                                  
                                );
                              case "price":
                                return (
                                  <TableCell key={colId} className={`text-right font-medium ${getVolumeChangeClass(((symbol.buyPrice + symbol.sellPrice) / 2), symbol.previousBuyPrice)}`}>
                                    {((symbol.buyPrice + symbol.sellPrice) / 2).toFixed(symbol.digit)}
                                  </TableCell>
                                );
                              case "buyPrice":
                                return (
                                  <TableCell key={colId} className={`text-right font-medium ${getVolumeChangeClass(symbol.buyPrice, symbol.previousBuyPrice)}`}>
                                    {symbol.buyPrice.toFixed(symbol.digit)}
                                  </TableCell>
                                );
                              case "sellPrice":
                                return (
                                  <TableCell key={colId} className={`text-right font-medium ${getVolumeChangeClass(symbol.sellPrice, symbol.previousSellPrice)}`}>
                                    {symbol.sellPrice.toFixed(symbol.digit)}
                                  </TableCell>
                                );
                              case "bidVolume":
                                return (
                                  <TableCell key={colId} className={`text-right transition-colors ${bidVolumeClass}`}>
                                    {formatVolume(symbol.bidVolume)}
                                  </TableCell>
                                );
                              case "askVolume":
                                return (
                                  <TableCell key={colId} className={`text-right transition-colors ${askVolumeClass}`}>
                                    {formatVolume(symbol.askVolume)}
                                  </TableCell>
                                );
                              case "high":
                                return (
                                  <TableCell key={colId} className="text-right">
                                    {symbol.high.toFixed(symbol.digit)}
                                  </TableCell>
                                );
                              case "low":
                                return (
                                  <TableCell key={colId} className="text-right">
                                    {symbol.low.toFixed(symbol.digit)}
                                  </TableCell>
                                );
                              case "open":
                                return (
                                  <TableCell key={colId} className="text-right">
                                    {openPrice}
                                  </TableCell>
                                );
                              case "close":
                                return (
                                  <TableCell key={colId} className="text-right">
                                    {closePrice}
                                  </TableCell>
                                );
                              case "lowerCircuit":
                                return (
                                  <TableCell key={colId} className="text-right text-negative">
                                    {lowerCircuit}
                                  </TableCell>
                                );
                              case "upperCircuit":
                                return (
                                  <TableCell key={colId} className="text-right text-positive">
                                    {upperCircuit}
                                  </TableCell>
                                );
                              case "spread":
                                return (
                                  <TableCell key={colId} className="text-right">
                                    {symbol.spread.toLocaleString('en-US', {
                                      minimumIntegerDigits: 2,
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    })}
                                  </TableCell>
                                );
                              case "absChange":
                                return (
                                  <TableCell key={colId} className={`text-right ${symbol.change >= 0 ? "text-positive" : "text-negative"}`}>
                                    {symbol.change >= 0 ? "+" : ""}{absoluteDifference.toFixed(symbol.digit)}
                                  </TableCell>
                                );
                              case "percentChange":
                                return (
                                  <TableCell key={colId} className={`text-right ${symbol.change >= 0 ? "text-positive" : "text-negative"}`}>
                                    <span className="flex items-center justify-end gap-1">
                                      {symbol.change >= 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                                      {symbol.change >= 0 ? "+" : ""}{symbol.change}%
                                    </span>
                                  </TableCell>
                                );
                              case "lastTrade":
                                return (
                                  <TableCell key={colId} className="text-right text-muted-foreground">
                                    {formattedTime}
                                  </TableCell>
                                );
                              case "expiry":
                                return (
                                  <TableCell key={colId} className="text-right text-muted-foreground">
                                    {(() => {
                                      const date = new Date();
                                      date.setDate(date.getDate() + 30);
                                      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                                    })()}
                                  </TableCell>
                                );
                              case "openInterest":
                                return (
                                  <TableCell key={colId} className="text-right text-muted-foreground">
                                    {openInterest}
                                  </TableCell>
                                );
                              case "volume":
                                return (
                                  <TableCell key={colId} className="text-right text-muted-foreground">
                                    {volume}
                                  </TableCell>
                                );
                              case "exchange":
                                return (
                                  <TableCell key={colId} className="text-right">
                                    {exchange}
                                  </TableCell>
                                );
                              default:
                                return <TableCell key={colId}>--</TableCell>;
                            }
                          })}
                        </TableRow>
                      )
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={orderedVisibleColumns.length + 1} className="h-32 text-center">
                        {searchTerm ? (
                          <div className="py-6 px-4 text-center">
                            <div className="flex flex-col items-center justify-center text-muted-foreground">
                              <Search className="h-12 w-12 mb-3 opacity-40" />
                              <p className="text-sm font-medium">No symbols found</p>
                              <p className="text-xs mt-1">Try a different search term</p>
                            </div>
                          </div>
                        ) : (
                          (isWatchlistPage || activeView === "favorites") ? 
                            "Your favorites is empty" : 
                            "No trading symbols found"
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
        
      )}
      
      {/* Mobile Card Layout */}
      {isMobile && (
        <div className="md:hidden space-y-0.5 ">
          <TradingCardHeader 
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={toggleSort}
            columnOrder={columnOrder}
            onColumnReorder={handleColumnReorder}
          />
          {visibleSymbols.length > 0 ? (
            visibleSymbols.map((symbol, index) => (
              <div 
                key={symbol.id} 
                className="animate-fade-in" 
                style={{ animationDelay: `${index % 20 * 30}ms` }}
              >
                <TradingCard 
                  symbol={symbol} 
                  isFavorite={isInWatchlist(symbol.name)}
                  onToggleFavorite={toggleFavorite}
                  hideStarInWatchlist={isWatchlistPage || activeView === "favorites"}
                  onAskAIClick={() => handleAskAIClick(symbol)}
                />
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              {(isWatchlistPage || activeView === "favorites") ? (
                <>
                  <p className="mb-3">Your favorites is empty</p>
                  <Button variant="outline" onClick={() => setDialogOpen(true)}>
                    Add Symbols
                  </Button>
                </>
              ) : searchTerm ? (
                <div className="py-6 px-4 text-center">
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <Search className="h-12 w-12 mb-3 opacity-40" />
                    <p className="text-sm font-medium">No symbols found</p>
                    <p className="text-xs mt-1">Try a different search term</p>
                  </div>
                </div>
              ) : (
                <p>No trading symbols found</p>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Table-like Compact View for Small Screens and Tablets */}
      <div className="md:hidden sm:block hidden">
        <div className="space-y-0.5 border rounded-lg overflow-hidden bg-background p-1.5">
          <TradingCardHeader 
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={toggleSort}
            columnOrder={columnOrder}
            onColumnReorder={handleColumnReorder}
          />
          
          {visibleSymbols.length > 0 ? (
            visibleSymbols.map((symbol, index) => (
              <div 
                key={symbol.id}
                className="animate-fade-in" 
                style={{ animationDelay: `${index % 20 * 30}ms` }}
              >
                <TradingCard 
                  symbol={symbol} 
                  isFavorite={isInWatchlist(symbol.name)}
                  onToggleFavorite={toggleFavorite}
                  isCompact={true}
                  hideStarInWatchlist={isWatchlistPage || activeView === "favorites"}
                  onAskAIClick={() => handleAskAIClick(symbol)}
                />
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-4 text-muted-foreground">
              {(isWatchlistPage || activeView === "favorites") ? (
                <>
                  <p className="mb-2">Your favorites is empty</p>
                  <Button variant="outline" onClick={() => setDialogOpen(true)}>
                    Add Symbols
                  </Button>
                </>
              ) : searchTerm ? (
                <div className="py-6 px-4 text-center">
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <Search className="h-12 w-12 mb-3 opacity-40" />
                    <p className="text-sm font-medium">No symbols found</p>
                    <p className="text-xs mt-1">Try a different search term</p>
                  </div>
                </div>
              ) : (
                <p>No trading symbols found</p>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* View More button */}
      {hasMoreSymbols && (
        <div className="flex justify-center mt-2 mb-2 view-more-btn">
          <Button 
            variant="outline" 
            className="w-full sm:w-auto" 
            onClick={handleViewMore}
          >
            View More
          </Button>
        </div>
      )}
      
      {filteredSymbols.length === 0 && activeView === "market" && !isWatchlistPage && (
        <div className="flex flex-col items-center justify-center py-4 sm:py-8">
          <p className="text-muted-foreground mb-2">No trading symbols found</p>
          <Button variant="outline" onClick={() => {
            setSearchTerm("");
            setInstrumentTypeFilter("all");
          }}>
            Clear Filters
          </Button>
        </div>
      )}

      {/* Chart Modal */}
      <Dialog open={chartModalOpen} onOpenChange={setChartModalOpen}>
        <DialogContent className="max-w-screen max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center">
              <span>Chart - {selectedSymbolForChart}</span>
              <div className="flex gap-2 mr-8">
                <Button 
                  asChild
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold"
                  size="sm"
                >
                  <a href="#">Buy</a>
                </Button>
                <Button 
                  asChild
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                  size="sm"
                >
                  <a href="#">Sell</a>
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div id="tradingview_widget" className="w-full h-[1000px]"></div>
        </DialogContent>
      </Dialog>
      
      {/* AI Chat Drawer */}
      {aiChatSymbol && (
        <SymbolAIChatDrawer
          open={isAIChatOpen}
          onOpenChange={setIsAIChatOpen}
          symbol={aiChatSymbol}
        />
      )}
    </div>
  );
};
